/**
 * Every stage of the agent has one expression (docs/plans/kivi-orb.md,
 * Decided 15, from the Kivi guide): one table, for both apps, from each stage
 * either app knows to what Kivi shows there. A stage shows a Kivi state, the
 * faceless disc, or no mark at all.
 *
 * - The faceless disc is Kivi at rest with no eyes and no line: a render
 *   option (`KiviFrameOptions.faceless`), not a fourteenth state. Errors and
 *   mismatches get plain words and no face: an error, a refusal, a failed or
 *   stopped turn, could not answer, not reached, offline, quota reached, the
 *   mic blocked, a money mismatch.
 * - No mark: nothing that carries money a person reads or a customer
 *   receives (a total, a balance, payroll, a bill) shows Kivi's face.
 * - Connecting and reconnecting rest dimmed, at the live look's 0.45.
 * - Done holds `KIVI_DONE_S`, then rests: the apps time it.
 *
 * A stage is named `place:stage`, the place being where its name comes from:
 * `live` the live look's states (`live/live-state.ts`), `turn` a chat turn,
 * `step` a step of a turn (`AgentStepState`), `proposal` a card's outcome
 * (`AgentProposalOutcomeState`, and `open` while none), `question` a question's
 * kind, `ask` the Ask OS button's states, `setup` the setup studio, `money`
 * what carries money. The person's own feelings are not stages.
 *
 * Every function is a worklet; the module imports only its own files.
 */
import type { KiviStateId } from './states';

/** What a stage shows. */
export type KiviOfStage =
  | { readonly kind: 'kivi'; readonly state: KiviStateId; readonly dim: number }
  | { readonly kind: 'faceless' }
  | { readonly kind: 'none' };

/** The live look's dimmed orb, as Kivi takes it: connecting and reconnecting. */
export const KIVI_DIM = 0.45;

const kivi = (state: KiviStateId, dim = 1): KiviOfStage => ({ kind: 'kivi', state, dim });
const FACELESS: KiviOfStage = { kind: 'faceless' };
const NONE: KiviOfStage = { kind: 'none' };

/** The table: every stage either app knows. */
export const KIVI_STAGES = {
  // The live look (LiveStateName).
  'live:ended': kivi('resting'),
  'live:mic_blocked': FACELESS,
  'live:connecting': kivi('resting', KIVI_DIM),
  'live:reconnecting': kivi('resting', KIVI_DIM),
  'live:interrupted': kivi('quiet'),
  'live:muted': kivi('quiet'),
  'live:needs_tap': kivi('asking'),
  'live:still_there': kivi('resting'),
  'live:rollover': kivi('listening'),
  'live:quiet': kivi('quiet'),
  'live:stopped': kivi('resting'),
  'live:speaking': kivi('speaking'),
  'live:working': kivi('thinking'),
  'live:listening': kivi('listening'),
  // A chat turn: the chat's own status, and how a turn ended.
  'turn:idle': kivi('resting'),
  'turn:submitted': kivi('thinking'),
  'turn:streaming': kivi('speaking'),
  'turn:typing': kivi('speaking'),
  'turn:ready': kivi('resting'),
  'turn:asks': kivi('asking'),
  'turn:error': FACELESS,
  'turn:failed': FACELESS,
  'turn:stopped': FACELESS,
  'turn:refused': FACELESS,
  'turn:could_not_answer': FACELESS,
  'turn:not_reached': FACELESS,
  'turn:unmet_noted': kivi('cant_yet'),
  // A step of a turn (AgentStepState).
  'step:running': kivi('thinking'),
  'step:done': kivi('resting'),
  'step:refused': FACELESS,
  'step:failed': FACELESS,
  'step:stopped': FACELESS,
  // A card the person decides (AgentProposalOutcomeState, and open while none).
  'proposal:open': kivi('asking'),
  'proposal:edit_first': kivi('asking'),
  'proposal:done': kivi('done'),
  'proposal:undone': kivi('resting'),
  'proposal:left': kivi('resting'),
  'proposal:stopped': FACELESS,
  'proposal:failed': FACELESS,
  // A question waiting on the person, by its kind.
  'question:need': kivi('asking'),
  'question:which': kivi('unsure'),
  // The Ask OS button (AskOsState; its import is `importing`).
  'ask:hidden': NONE,
  'ask:idle': kivi('resting'),
  'ask:touch': kivi('touch'),
  'ask:offline': FACELESS,
  'ask:working': kivi('thinking'),
  'ask:importing': kivi('thinking'),
  // Setup.
  'setup:first_open': kivi('hello'),
  'setup:building': kivi('building'),
  'setup:built': kivi('done'),
  'setup:unsupported': kivi('cant_yet'),
  'setup:quota': FACELESS,
  'setup:down': FACELESS,
  // Anywhere.
  error: FACELESS,
  'change:saved': kivi('done'),
  'do_not_disturb': kivi('silent'),
  offline: FACELESS,
  quota: FACELESS,
  'mic:blocked': FACELESS,
  'money:mismatch': FACELESS,
  // What carries money: no face beside it.
  'money:total': NONE,
  'money:balance': NONE,
  'money:payroll': NONE,
  'money:customer': NONE,
} as const satisfies Readonly<Record<string, KiviOfStage>>;

/** Every stage either app knows. */
export type KiviStage = keyof typeof KIVI_STAGES;

/** What `stage` shows: a Kivi state (and how dimmed), the faceless disc, or no mark. */
export function kiviOfStage(stage: KiviStage): KiviOfStage {
  'worklet';
  return KIVI_STAGES[stage];
}

/**
 * Whether a finished reply asks: its last sentence ends in a question mark
 * (the guide's rule; Decided 15). It shows asking until the person answers.
 * Closing quotes, brackets and markdown emphasis after the mark still count.
 */
export function kiviEndsInQuestion(words: string | null | undefined): boolean {
  'worklet';
  if (words === null || words === undefined) return false;
  const trimmed = words.replace(/[\s"'”’)\]*_`]+$/u, '');
  return trimmed.endsWith('?') || trimmed.endsWith('？');
}
